
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Users, DollarSign, Star } from "lucide-react";
import { Link } from "react-router-dom";

const steps = [
  {
    icon: Users,
    title: "Create Your Profile",
    description: "Sign up and build a compelling profile that showcases your skills, experience, and personality."
  },
  {
    icon: Star,
    title: "Define Your Services",
    description: "List the services you offer, set competitive rates, and specify your availability."
  },
  {
    icon: CheckCircle,
    title: "Get Verified",
    description: "Complete our verification process to build trust with potential clients."
  },
  {
    icon: DollarSign,
    title: "Start Earning",
    description: "Begin accepting projects and building your reputation on the platform."
  }
];

const tips = [
  "Use a professional profile photo",
  "Write a clear, engaging description of your skills",
  "Set competitive but fair pricing",
  "Respond to inquiries promptly",
  "Deliver quality work on time",
  "Ask for reviews from satisfied clients"
];

const HowToStart = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="font-poppins font-bold text-4xl md:text-5xl mb-4">How to Start as a Provider</h1>
          <p className="font-inter text-xl text-gray-600 max-w-3xl mx-auto">
            Join our community of skilled providers and start building your freelance career today.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {steps.map((step, index) => (
            <Card key={index} className="h-full">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <step.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="font-poppins">{step.title}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="font-inter text-gray-600">
                  {step.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <Card>
            <CardHeader>
              <CardTitle className="font-poppins text-2xl">Success Tips</CardTitle>
              <CardDescription className="font-inter">
                Follow these tips to maximize your success on the platform.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {tips.map((tip, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="font-inter text-gray-700">{tip}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-poppins text-2xl">Ready to Get Started?</CardTitle>
              <CardDescription className="font-inter">
                Join thousands of providers who are already earning on our platform.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button asChild className="w-full" size="lg">
                <Link to="/offer-service">Create Your Profile</Link>
              </Button>
              <Button asChild variant="outline" className="w-full" size="lg">
                <Link to="/discover-services">Browse Services</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HowToStart;
